import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { LogStream } from '@/components/progress/LogStream';
import { migrationsApi, migrationKeys } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { MassMigrationBatchOrgDto, MassMigrationModuleStatusDto } from '@/types/massMigration';
import { OrgStatusBadge } from './OrgStatusBadge';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MIGRATE_STATUS_CONFIG: Record<string, { label: string; variant: 'success' | 'destructive' | 'warning' | 'secondary' | 'info' | 'outline' }> = {
  Pending:    { label: 'Pending',     variant: 'secondary' },
  InProgress: { label: 'In Progress', variant: 'info' },
  Succeeded:  { label: 'Succeeded',   variant: 'success' },
  Failed:     { label: 'Failed',      variant: 'destructive' },
  Skipped:    { label: 'Skipped',     variant: 'outline' },
};

const PROVISION_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  NotApplicable: { label: '—',         className: 'text-muted-foreground' },
  Pending:       { label: 'Pending',   className: 'text-warning' },
  Succeeded:     { label: 'OK',        className: 'text-success' },
  Failed:        { label: 'Failed',    className: 'text-destructive' },
};

function ModuleRow({ mod }: { mod: MassMigrationModuleStatusDto }) {
  const migrateConfig = MIGRATE_STATUS_CONFIG[mod.migrateStatus] ?? { label: mod.migrateStatus, variant: 'secondary' as const };
  const provisionConfig = PROVISION_STATUS_CONFIG[mod.amplifyProvisioningStatus] ?? { label: mod.amplifyProvisioningStatus, className: '' };

  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-4 py-2.5 text-sm font-mono">{mod.moduleKey}</td>
      <td className="px-4 py-2.5">
        <Badge variant={migrateConfig.variant}>{migrateConfig.label}</Badge>
      </td>
      <td className={cn('px-4 py-2.5 text-xs', provisionConfig.className)}>
        {provisionConfig.label}
      </td>
      <td className="px-4 py-2.5 text-xs text-destructive max-w-xs truncate" title={mod.migrateError ?? undefined}>
        {mod.migrateError ?? ''}
      </td>
    </tr>
  );
}

// ─── OrgDetailView ────────────────────────────────────────────────────────────

interface OrgDetailViewProps {
  org: MassMigrationBatchOrgDto;
  batchName: string;
  onBackToBatch?: (() => void) | undefined;
}

export function OrgDetailView({ org, batchName, onBackToBatch }: OrgDetailViewProps) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: migrationKeys.logs(org.migrationId),
    queryFn: () => migrationsApi.getLogs(org.migrationId),
    staleTime: Infinity,
  });
  const logs = data?.items ?? [];
  const errorCount = logs.filter((l) => l.level === 'Error').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-1">
        {onBackToBatch && (
          <button
            type="button"
            onClick={onBackToBatch}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← {batchName}
          </button>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-xl font-semibold">{org.organizationName}</h2>
          <OrgStatusBadge status={org.status} />
          <Link
            to="/migrations/$migrationId"
            params={{ migrationId: org.migrationId }}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            View migration details
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>
        <p className="text-xs font-mono text-muted-foreground">{org.bridgeEntityUuid}</p>
        {org.amplifyOrganizationUuid && (
          <p className="text-xs text-muted-foreground">
            Amplify org: <span className="font-mono">{org.amplifyOrganizationUuid}</span>
          </p>
        )}
      </div>

      {/* Module status table */}
      <div className="space-y-2">
        <span className="text-sm font-medium">Modules</span>
        {org.modules.length === 0 ? (
          <p className="text-sm text-muted-foreground">No modules listed for this organization.</p>
        ) : (
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-sm">
              <caption className="sr-only">Module statuses for {org.organizationName}</caption>
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Module</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Migrate</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Provisioning</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Error</th>
                </tr>
              </thead>
              <tbody>
                {org.modules.map((mod) => (
                  <ModuleRow key={mod.moduleKey} mod={mod} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Migration logs */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Migration Logs</span>
          {logs.length > 0 && (
            <div className="flex items-center gap-2">
              {errorCount > 0 && (
                <Badge variant="destructive">{errorCount} error{errorCount !== 1 ? 's' : ''}</Badge>
              )}
              <Badge variant="secondary">{logs.length} entries</Badge>
            </div>
          )}
        </div>
        {isLoading ? (
          <LoadingSpinner size="sm" label="Loading logs..." />
        ) : isError ? (
          <ErrorMessage error={error} title="Failed to load logs" onRetry={() => void refetch()} />
        ) : logs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No log entries recorded for this migration.</p>
        ) : (
          <LogStream logs={logs} moduleKeys={org.moduleKeys} maxHeight="320px" />
        )}
      </div>
    </div>
  );
}
